import classnames from 'classnames';
import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { Label, Checkbox, Stack } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SpanWithComponent, ErrorString } from '@bingads-webui-campaign-react/wizard-shared-components';

export const isAdFormatValid = adFormat => adFormat.HPA || adFormat.PPA;

export const defaultAdFormat = {
  HPA: true,
  PPA: true,
};

const CheckBox = (props) => {
  const {
    infoText,
    field,
    setAdFormat,
    isChecked,
    helpId,
    adFormat,
  } = props;
  const i18n = useContext(I18nContext);
  const onChange = useCallback((event, checked) => {
    setAdFormat({
      ...adFormat,
      [field]: checked,
      isDirty: true,
    });
  }, [adFormat, field, setAdFormat]);

  return (
    <Checkbox
      onRenderLabel={() => (
        <SpanWithComponent
          SpanText={infoText}
          Component={() => (
            <HelpPopup
              topic={helpId}
              aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: infoText })}
            />)}
        />)}
      checked={isChecked(field)}
      onChange={onChange}
      className={field}
    />);
};

CheckBox.propTypes = {
  infoText: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  setAdFormat: PropTypes.func.isRequired,
  isChecked: PropTypes.func.isRequired,
  helpId: PropTypes.string.isRequired,
  adFormat: PropTypes.shape({}).isRequired,
};

const getStyles = () => ({
  HotelAdFormat: {
    '& .description': {
      marginBottom: '12px',
    },
  },
});

export const AdFormat = React.memo((props) => {
  const {
    setAdFormat,
    adFormat,
    scenario,
    permissions,
  } = props;
  const i18n = useContext(I18nContext);
  const HPAText = i18n.getString(_TL_('Hotel price ad (HPA)'));
  const PPAText = i18n.getString(_TL_('Property promotion ad (PPA)'));
  const classes = useDefaultStyles(getStyles);
  const isChecked = useCallback(key => adFormat[key] === true, [adFormat]);
  const IsRenameHotelToLodgingEnabled = permissions && permissions.dynamic && permissions.dynamic.IsRenameHotelToLodgingEnabled;

  return (
    <div className={classnames('hotel-ad-format', 'section', classes.HotelAdFormat)}>
      <div className="title">
        <Label>
          {IsRenameHotelToLodgingEnabled ? i18n.getString(_TL_('Property ad format')) : i18n.getString(_TL_('Hotel ad format'))}
        </Label>
        <div className="description">
          {IsRenameHotelToLodgingEnabled ? i18n.getString(_TL_('Determines the ad type you want to create for this property ad group.')) : i18n.getString(_TL_('Determines the ad type you want to create for this hotel ad group.'))}
        </div>
      </div>
      <div className="checkboxes">
        <Stack tokens={{ childrenGap: 12 }}>
          <CheckBox key="HPA" field="HPA" infoText={HPAText} helpId="pop_BA_HotelPriceAds" isChecked={isChecked} adFormat={adFormat} setAdFormat={setAdFormat} scenario={scenario} />
          <CheckBox key="PPA" field="PPA" infoText={PPAText} helpId="pop_BA_PropertyPromotionAds" isChecked={isChecked} adFormat={adFormat} setAdFormat={setAdFormat} scenario={scenario} />
        </Stack>
      </div>
      {!isAdFormatValid(adFormat) &&
        <ErrorString error={i18n.getString(_TL_('At least one ad format option must be selected'))} />}
    </div>);
});

AdFormat.propTypes = {
  adFormat: PropTypes.shape({}),
  setAdFormat: PropTypes.func.isRequired,
  scenario: PropTypes.string.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      IsRenameHotelToLodgingEnabled: PropTypes.bool,
    }),
  }),
};

AdFormat.defaultProps = {
  adFormat: {},
  permissions: {
    dynamic: {
      IsRenameHotelToLodgingEnabled: false,
    },
  },
};

